import { Modal, ModalBody, ModalContent } from "@/components/ui/modal.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTeamSegment } from "../../hooks/useTeamSegment.ts";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { ManageSegmentsEditorPanel } from "./ManageSegmentsEditorPanel.tsx";
import { ManageSegmentsSidebar } from "./ManageSegmentsSidebar.tsx";
import type { TeamSegmentFormHandle } from "./TeamSegmentForm.tsx";

export interface ManageSegmentsModalProps {
    panel: string | null;
    onPanelChange: (panel: string | null) => void;
}

export function ManageSegmentsModal({ panel, onPanelChange: setPanel }: ManageSegmentsModalProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ManageSegmentsModal", { i18n: i18nextInstance });
    const [, setTeamSegment] = useTeamSegment();
    const editorRef = useRef<TeamSegmentFormHandle>(null);

    const requestPanelChange = useCallback(
        (next: string | null) => {
            if (!editorRef.current) {
                setPanel(next);

                return;
            }

            editorRef.current.requestLeave(() => setPanel(next));
        },
        [setPanel]
    );

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) {
                requestPanelChange(null);
            }
        },
        [requestPanelChange]
    );
    const handleSelect = useCallback((key: string) => requestPanelChange(key), [requestPanelChange]);
    const handleDeleted = useCallback(() => setPanel("new"), [setPanel]);
    const handleCreated = useCallback((segmentId: string) => setPanel(segmentId), [setPanel]);
    const handleApply = useCallback(
        (segmentId: string) => {
            void setTeamSegment(segmentId);
            setPanel(null);
        },
        [setTeamSegment, setPanel]
    );
    const handleFilterApplied = useCallback(() => setPanel(null), [setPanel]);

    if (panel === null) {
        return null;
    }

    return (
        <Modal isOpen onOpenChange={handleOpenChange}>
            <ModalContent size="5xl" aria-label={t("title")}>
                {/* intent-ui-deviation: DialogBody's default single padded column overridden to a row so the sidebar and editor sit side by side.
                    intent-ui-deviation: fixed height, no scale token for it. `flex-none` is load-bearing -- DialogBody's default flex-1 (flex-basis: 0%) leaves this no definite height to grow from, so h-[36rem] alone would silently shrink to content. */}
                <ModalBody className="h-[42rem] flex-none flex-row gap-0 overflow-hidden p-0">
                    <div className="h-full w-64 shrink-0 overflow-hidden border-e">
                        <ManageSegmentsSidebar selectedKey={panel} onSelect={handleSelect} />
                    </div>
                    <div className="min-w-0 flex-1 overflow-y-auto">
                        <ManageSegmentsEditorPanel ref={editorRef} panel={panel} onDone={handleDeleted} onCreated={handleCreated} onApply={handleApply} onFilterApplied={handleFilterApplied} />
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
